/* Contractor Portal — Fleet Map dashboard. Polls /contractor/fleet every 15 s. */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Truck, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { FleetKpis } from "@/components/contractor/FleetKpis";
import { FleetTruckRow } from "@/components/contractor/FleetTruckRow";
import { ContractorFleetMap } from "@/components/contractor/ContractorFleetMap";

const POLL_MS = 15_000;
const ORDER = { en_route: 0, on_site: 1, discharging: 1, returning: 2, loading: 3, slumping: 3, available: 4, maintenance: 5, out_of_service: 5 };

export default function ContractorFleet() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(null);
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/contractor/fleet");
      setData(r.data); setErr("");
    } catch (e) { setErr(e.response?.data?.detail || "Could not load fleet"); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, POLL_MS); return () => clearInterval(t); }, [load]);
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 30_000); return () => clearInterval(t); }, []);

  const trucks = useMemo(() => [...(data?.trucks || [])].sort((a, b) => (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9) || a.number.localeCompare(b.number)), [data]);

  return (
    <div className="h-full flex flex-col" data-testid="contractor-fleet-page">
      <div className="px-4 lg:px-6 pt-5 pb-3 space-y-3 border-b border-[#22262E] bg-[#0F1216]">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.25em] text-[#8B909A]">LIVE · REFRESHES EVERY 15 S</div>
            <h1 className="heading text-2xl sm:text-3xl font-black uppercase text-white">Fleet <span className="text-[#38BDF8]">Map</span></h1>
          </div>
          <div className="flex items-center gap-2 text-[10px] mono text-[#8B909A]">
            {data?.generated_at && <span data-testid="ctr-fleet-updated">Updated {new Date(data.generated_at).toLocaleTimeString()}</span>}
            <button onClick={load} data-testid="ctr-fleet-refresh" className="inline-flex items-center gap-1 border border-[#2C313A] px-2 py-1 hover:border-[#38BDF8] hover:text-[#38BDF8]"><RefreshCw className="w-3 h-3" /> Refresh</button>
          </div>
        </div>
        <FleetKpis summary={data?.summary} />
      </div>

      {err && <div className="m-4 p-3 border border-red-900 bg-red-950/30 text-red-300 text-sm" data-testid="ctr-fleet-error">{err}</div>}

      <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
        <div className="lg:w-[420px] lg:shrink-0 lg:border-r border-[#22262E] overflow-y-auto p-3 space-y-2 max-h-[45vh] lg:max-h-none" data-testid="ctr-fleet-list">
          {data && trucks.length === 0 && (
            <div className="border border-dashed border-[#2C313A] p-6 text-center text-sm text-[#A1A6B0]" data-testid="ctr-fleet-empty">
              <Truck className="w-8 h-8 mx-auto mb-3 text-[#38BDF8]" />
              No trucks linked yet. Register your regos under <b>My Trucks</b> — units appear here as soon as they match a plant's fleet record.
              <div className="mt-3"><Link to="/contractor/trucks" data-testid="ctr-fleet-empty-cta" className="inline-flex items-center gap-1 text-[#38BDF8] hover:underline">Register trucks <ArrowRight className="w-3 h-3" /></Link></div>
            </div>
          )}
          {trucks.map((t) => <FleetTruckRow key={`${t.id}-${tick}`} t={t} selected={selected === t.id} onSelect={setSelected} />)}
        </div>
        <div className="flex-1 min-h-[45vh] lg:min-h-0 relative" data-testid="ctr-fleet-map">
          <ContractorFleetMap trucks={trucks} plants={data?.plants || []} selectedId={selected} onSelect={setSelected} />
        </div>
      </div>
    </div>
  );
}
